import { apiClient } from '@/shared/api/client';
import { API_ENDPOINTS } from '@/shared/constants/api/endpoints.ts';

import type {
  DocDetail,
  DocFeedbackList,
  DocInput,
  DocSection,
  DocsTree,
  DocSummary,
  FeedbackInput,
  ReorderInput,
  SectionInput,
} from '../model/docs.type';

/**
 * 도움말 조회 호출부.
 *
 * `manage` 는 **경로 자체를 바꾼다**(쿼리 플래그가 아니다). Gateway 의
 * JwtAuthMiddleware 가 public path 를 만나면 x-user-* 헤더를 심기 전에 통과시키기
 * 때문에, 공개 경로에 도달한 요청은 서버가 로그인 여부를 알 수 없다.
 * 초안이 필요한 관리 화면은 인증이 걸린 `/manage/*` 로 간다.
 *
 * 공개 경로는 `skipAuthRedirect` 를 켠다 — 비로그인 방문자가 도움말을 보다가
 * 로그인 화면으로 튕기면 안 된다(게시판 공개 조회와 같은 규약).
 */
export const docsApi = {
  /** 섹션+문서 트리 + 리다이렉트 표. 본문(body)은 포함되지 않는다. */
  getTree: async (manage = false): Promise<DocsTree> => {
    const { data } = await apiClient.get<DocsTree>(
      manage ? API_ENDPOINTS.docs.manageTree : API_ENDPOINTS.docs.tree,
      manage ? undefined : { skipAuthRedirect: true },
    );
    return data;
  },

  /** 문서 상세(본문 포함). */
  getArticle: async (
    section: string,
    slug: string,
    manage = false,
  ): Promise<DocDetail> => {
    const { data } = await apiClient.get<DocDetail>(
      manage ? API_ENDPOINTS.docs.manageArticle : API_ENDPOINTS.docs.article,
      manage
        ? { params: { section, slug } }
        : { params: { section, slug }, skipAuthRedirect: true },
    );
    return data;
  },

  /**
   * "이 문서가 도움이 되었나요?" 접수.
   *
   * 공개 경로라 `skipAuthRedirect` 를 켠다 — 비로그인 독자가 의견을 남기다
   * 로그인 화면으로 튕기면 안 된다. 서버는 204 만 돌려주고 집계는 주지 않는다.
   */
  submitFeedback: async (input: FeedbackInput): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.docs.feedback, input, {
      skipAuthRedirect: true,
    });
  },

  /* ── 관리(ADMIN) ─────────────────────────────────────────── */

  /** 편집용 단건. 초안 포함이라 공개 상세와 경로가 다르다. */
  getForEdit: async (id: string): Promise<DocDetail> => {
    const { data } = await apiClient.get<DocDetail>(
      API_ENDPOINTS.docs.adminDetail(id),
    );
    return data;
  },

  createDoc: async (input: DocInput): Promise<DocSummary> => {
    const { data } = await apiClient.post<DocSummary>(
      API_ENDPOINTS.docs.createDoc,
      input,
    );
    return data;
  },

  updateDoc: async (id: string, input: DocInput): Promise<DocSummary> => {
    const { data } = await apiClient.put<DocSummary>(
      API_ENDPOINTS.docs.updateDoc(id),
      input,
    );
    return data;
  },

  removeDoc: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.docs.removeDoc(id));
  },

  createSection: async (input: SectionInput): Promise<DocSection> => {
    const { data } = await apiClient.post<DocSection>(
      API_ENDPOINTS.docs.createSection,
      input,
    );
    return data;
  },

  updateSection: async (id: string, input: SectionInput): Promise<DocSection> => {
    const { data } = await apiClient.put<DocSection>(
      API_ENDPOINTS.docs.updateSection(id),
      input,
    );
    return data;
  },

  removeSection: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.docs.removeSection(id));
  },

  /** '아니오' 사유 목록. */
  getFeedback: async (docId: string): Promise<DocFeedbackList> => {
    const { data } = await apiClient.get<DocFeedbackList>(
      API_ENDPOINTS.docs.manageFeedback,
      { params: { docId } },
    );
    return data;
  },

  /** 순서 재배치. 드래그 한 번 = 이 호출 하나(부분 실패로 순서가 반쯤 뒤집히지 않게). */
  reorder: async (input: ReorderInput): Promise<void> => {
    await apiClient.put(API_ENDPOINTS.docs.reorder, input);
  },
};
